import { Observable } from 'rxjs';
import { RockThumbnailService } from '../services/rock-thumbnail/rock-thumbnail.service';
import { RockImagesService } from '../services/rock-images/rock-images.service';
import { RockFile } from '../core/models/rock-files.model';

export interface RockFileLoadResult {
  thumbnailUrl: string | null;
  metadata: RockFile | null;
}

/**
 * Utility class for loading rock file thumbnail and metadata.
 * Handles the common pattern of loading both independently.
 * Returns observables that should be managed by the component's lifecycle.
 */
export class RockFileLoader {
  constructor(
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly rockImagesService: RockImagesService,
  ) {}

  /**
   * Gets the thumbnail observable for the given file name.
   * Component should subscribe and manage the lifecycle.
   * @param fileName - The rock file name
   * @param fileType - Optional file type (e.g., 'Upscaling')
   * @returns Observable of thumbnail URL
   */
  getThumbnail(fileName: string, fileType?: string): Observable<string | null> {
    return this.rockThumbnailService.getThumbnail(fileName, fileType);
  }

  /**
   * Gets the metadata observable for the given file name.
   * Component should subscribe and manage the lifecycle.
   * @param fileName - The rock file name
   * @returns Observable of rock file metadata
   */
  getMetadata(fileName: string): Observable<RockFile> {
    return this.rockImagesService.getRockFileMetadata(fileName);
  }

  /**
   * Helper method to load both thumbnail and metadata with proper callbacks.
   * Returns the observables for the component to manage lifecycle with takeUntilDestroyed().
   * @param fileName - The rock file name
   * @param onThumbnailLoad - Callback when thumbnail loads
   * @param onMetadataLoad - Callback when metadata loads
   * @param onMetadataError - Optional callback when metadata fails to load
   * @returns Object with thumbnail and metadata observables
   */
  loadThumbnailAndMetadata(
    fileName: string,
    onThumbnailLoad: (url: string) => void,
    onMetadataLoad: (metadata: RockFile) => void,
    onMetadataError?: () => void
  ): { thumbnail$: Observable<string | null>; metadata$: Observable<RockFile> } {
    const thumbnail$ = this.getThumbnail(fileName);
    const metadata$ = this.getMetadata(fileName);

    // Set up thumbnail subscription handlers
    thumbnail$.subscribe({
      next: (url) => {
        if (url) {
          onThumbnailLoad(url);
        }
      }
    });

    // Set up metadata subscription handlers
    metadata$.subscribe({
      next: (metadata) => {
        onMetadataLoad(metadata);
      },
      error: () => {
        if (onMetadataError) {
          onMetadataError();
        }
      }
    });

    return { thumbnail$, metadata$ };
  }

  /**
   * Revokes a thumbnail URL to free up memory.
   * @param thumbnailUrl - The URL to revoke
   */
  revokeThumbnailUrl(thumbnailUrl: string): void {
    this.rockThumbnailService.revokeThumbnailUrl(thumbnailUrl);
  }
}
